import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  exchangeSsoTicket,
  login as loginRequest,
  register as registerRequest,
} from '@/api/auth';
import { setAuthToken, setUnauthorizedHandler } from '@/api/client';

const AuthContext = createContext(null);

const STORAGE_KEY = 'tub.session';

/**
 * Reads a saved session and arms the API client with its token.
 *
 * Called from the state initializer so the token is in place before the first
 * request goes out — a useEffect would run after that render, and any request
 * fired during it would go out unauthenticated.
 */
function restoreSession() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    setAuthToken(saved?.access_token);
    return saved;
  } catch {
    return null;
  }
}

/**
 * The signed-in playTUB account.
 *
 * Books bought here are unlocked against a playTUB login, so a purchase can't
 * complete anonymously — checkout asks for credentials, they're validated by the
 * external auth service, and the session that comes back is what the order is
 * filed under. Signing in is not tied to buying, though: it is offered from the
 * header, and it is what makes a buyer's own purchase history readable.
 *
 * localStorage, so the session survives closing the tab — a reader who bought a
 * book last week should still be signed in to open it. The token carries its own
 * expiry and the server is the judge of it: the session is dropped the moment a
 * request comes back 401, rather than being trusted until then.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(restoreSession);

  // Both ways in end here: a password typed into the login dialog, and a handoff
  // ticket from playTUB. What the server hands back is identical either way, so
  // adopting it is one path rather than two that must be kept in step.
  const adopt = useCallback((next) => {
    setAuthToken(next.access_token);
    setSession(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — the in-memory session still covers this visit */
    }
    return next.user;
  }, []);

  const signIn = useCallback(
    async (credentials) => adopt(await loginRequest(credentials)),
    [adopt],
  );

  /**
   * Create a playTUB account and open a session on it.
   *
   * Two calls, because the server does two separable things: the account is
   * created on the external auth service, and the session comes from signing in
   * to it — the same /login a returning reader goes through, so there is one
   * path to a session rather than two to keep in step.
   *
   * If the sign-in is what fails, the account still exists. That is flagged on
   * the error rather than swallowed, so the caller can send the reader to the
   * sign-in form instead of asking them to register a name that is now taken.
   */
  const signUp = useCallback(
    async (details) => {
      await registerRequest(details);
      try {
        return adopt(await loginRequest({ username: details.username, password: details.password }));
      } catch (err) {
        err.accountCreated = true;
        throw err;
      }
    },
    [adopt],
  );

  /** Sign in from a playTUB handoff ticket — see pages/Sso. */
  const signInWithTicket = useCallback(
    async (ticket, options) => adopt(await exchangeSsoTicket(ticket, options)),
    [adopt],
  );

  const signOut = useCallback(() => {
    setAuthToken(null);
    setSession(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing saved to clear */
    }
  }, []);

  // A restored session is only a claim until the server rules on it. Any request
  // rejected as unauthenticated ends it, so an expired token clears itself
  // instead of leaving the UI pretending someone is signed in.
  useEffect(() => {
    setUnauthorizedHandler(signOut);
    return () => setUnauthorizedHandler(null);
  }, [signOut]);

  const value = useMemo(
    () => ({
      user: session?.user ?? null,
      isSignedIn: Boolean(session?.access_token),
      signIn,
      signUp,
      signInWithTicket,
      signOut,
    }),
    [session, signIn, signUp, signInWithTicket, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
